/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {SettlementStatusEnum} from '../../models/settlement-status.enum';
import {SettlementDetailModel} from '../../models/settlement-detail/settlement-detail.model';
import {SettlementDetailConfigurationModel} from './models/settlement-detail-configuration.model';
import {Injectable} from '@angular/core';
import SETTLEMENT_DISPLAY_CONFIGURATION_CONSTANTS from './settlement-display-constants';
import {SettlementDetailFooterConfigurationModel} from './models/settlement-detail-footer-configuration.model';
import {SettlementDetailAgreementConfigurationModel} from './models/settlement-detail-agreement-configuration.model';
import {SettlementDetailHeaderConfigurationModel} from './models/settlement-detail-header-configuration.model';
import {SettlementDetailInformationConfigurationModel} from './models/settlement-detail-information-configuration.model';
import {SettlementDetailAgreementTableDataModel} from '../../models/settlement-detail-agreement-table-data.model';
import {SettlementPositionBaseContract} from '../../api/contracts/settlement-position-base.contract';
import {CurrencyBaseContract} from '../../api/contracts/currency-base.contract';
import {SettlementDetailAgreementStatusEnum} from './models/settlement-detail-agreement-status.enum';
import {AvailableActions} from '../../api/contracts/available-actions.enum';
import {SettlementStatusEnumMapper} from '../../models/settlement-status-enum.mapper';

@Injectable({
    providedIn: 'root'
})
export class SettlementDetailSectionsConfigurationService {

    private readonly colorStatus: ReadonlyMap<SettlementStatusEnum, string> = new Map<SettlementStatusEnum, string>([
        [SettlementStatusEnum.ACCEPTED, 'accepted'],
        [SettlementStatusEnum.PENDING, 'pending'],
        [SettlementStatusEnum.REJECTED, 'rejected']
    ]);

    /**
     * For a given Settlement detail returns its display configuration
     * @param settlementDetail the {@link SettlementDetailModel} for the Settlement
     */
    retrievesSettlementDetailSectionsConfiguration(settlementDetail: SettlementDetailModel):
            SettlementDetailConfigurationModel {
        let configuration: SettlementDetailConfigurationModel = <SettlementDetailConfigurationModel>{};
        if (settlementDetail != null) {
            configuration = {
                header: this.getHeaderConfiguration(settlementDetail),
                information: this.getInformationConfiguration(settlementDetail),
                agreement: this.getAgreementConfiguration(settlementDetail),
                footer: this.getFooterConfiguration(settlementDetail)
            };
        }
        return configuration;
    }

    /**
     * For a given Settlement detail returns its header section configuration
     * @param settlementDetail the {@link SettlementDetailModel} for the Settlement
     */
    private getHeaderConfiguration(settlementDetail: SettlementDetailModel): SettlementDetailHeaderConfigurationModel {
        return {
            data: settlementDetail,
            styleClass: this.getHeaderCssStyle(<SettlementStatusEnum>settlementDetail.status?.code)
        };
    }

    private getHeaderCssStyle(statusCode: SettlementStatusEnum): string {
        return this.colorStatus.get(statusCode);
    }

    /**
     * For a given Settlement detail returns its information body section configuration
     * @param settlementDetail the {@link SettlementDetailModel} for the Settlement
     */
    private getInformationConfiguration(settlementDetail: SettlementDetailModel): SettlementDetailInformationConfigurationModel {
        return {
            status: SettlementStatusEnumMapper.fromString(settlementDetail.status.code),
            complaintDetail: settlementDetail.complaintDescription,
            supplierDetail: settlementDetail.supplier,
            materialDetail: settlementDetail.material,
            lastPendingDate: settlementDetail.lastPendingDate,
            lastChangeDateTime: settlementDetail.lastChangeDatetime
        };
    }

    /**
     * For a given settlement detail returns its agreement section configuration
     * @param settlementsDetail the {@link settlementDetailModel} for the settlement
     */
    private getAgreementConfiguration(settlementsDetail: SettlementDetailModel): SettlementDetailAgreementConfigurationModel {
        return {
            title: this.getAgreementTitle(1,
                    SettlementDetailAgreementStatusEnum[settlementsDetail.status.code as keyof typeof SettlementDetailAgreementStatusEnum]),
            agreementStatus: <SettlementStatusEnum>settlementsDetail.status.code,
            data: {
                tableData: this.getAgreementTable(settlementsDetail.settlementPositions, settlementsDetail.currency),
                contactManager: settlementsDetail.contactManager,
                materialsTotalGrossCost: settlementsDetail.materialsTotalGrossCost,
                teMinutesTotalCost: settlementsDetail.teMinutesTotalCost,
                settlementTotalCost: settlementsDetail.settlementTotalCost,
                currency: settlementsDetail.currency,
                decision: {
                    decidedBy: settlementsDetail.decidedBy,
                    decisionReason: settlementsDetail.decisionReason
                }
            }
        };
    }

    private getAgreementTitle(count: number, status?: SettlementDetailAgreementStatusEnum): string {
        return status != null ? `Agreement ${count} (${status})` : `Agreement ${count}`;
    }

    private getAgreementTable(settlementPositions: SettlementPositionBaseContract[], currency: CurrencyBaseContract):
            SettlementDetailAgreementTableDataModel[] {
        const lines: SettlementDetailAgreementTableDataModel[] = [];

        settlementPositions?.forEach((settlementPosition) => {
            const itemPosition: SettlementDetailAgreementTableDataModel = {
                quantity: settlementPosition.quantity,
                materialsUnitNetPrice: settlementPosition.materialsUnitNetPrice,
                materialsTotalNetCost: settlementPosition.materialsTotalNetCost,
                taxRate: settlementPosition.taxRate,
                materialsTotalGrossCost: settlementPosition.materialsTotalGrossCost,
                teMinutesAmount: settlementPosition.teMinutesAmount,
                teMinutesUnitCost: settlementPosition.teMinutesUnitCost,
                teMinutesAdditionalCosts: settlementPosition.teMinutesAdditionalCosts,
                teMinutesTotalCost: settlementPosition.teMinutesTotalCost,
                blueFapiaoNumber: settlementPosition.blueFapiaoNumber,
                deliveryNumber: settlementPosition.deliveryNumber,
                comments: settlementPosition.comments,
                currency: currency
            };
            lines.push(itemPosition);
        });

        return lines;
    }

    /**
     * For a given Settlement detail returns its footer section configuration
     * @param settlementsDetail the {@link SettlementDetailModel} for the Settlement
     */
    private getFooterConfiguration(settlementsDetail: SettlementDetailModel): SettlementDetailFooterConfigurationModel {
        const configuration: SettlementDetailFooterConfigurationModel = SETTLEMENT_DISPLAY_CONFIGURATION_CONSTANTS.footer.settlementDetail;

        configuration.primaryButton.disable = !settlementsDetail?.availableActions?.includes(AvailableActions.ACCEPT);
        configuration.secondaryButton.disable = !settlementsDetail?.availableActions?.includes(AvailableActions.REJECT);

        return configuration;
    }
}
